import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { customersAPI } from '../services/api';
import { fetchAll, create as createItem, update as updateItem, deleteItem } from '../services/apiHelpers';

/**
 * Customer Store - إدارة حالة العملاء
 *
 * Protocols Applied (From Father DB via Gemini Flash 2.5):
 * - P-STATE-001: Zustand State Management
 * - P034: Data Validation
 * - P010: Error Handling
 * - P025: Cache Management (LocalStorage persist)
 * - P-DATA-001: Clear Data Structure
 * - P016: Zero Duplication
 * - P-API-001: RESTful API Integration
 * - P-OFFLINE-001: Offline-First Strategy
 * - CUST-001: Customer CRUD operations
 * - CUST-002: Credit limit management
 * - CUST-003: Payment history tracking
 * - CUST-004: Customer balance calculation
 */

const useCustomerStore = create(
  persist(
    (set, get) => ({
      // State
      customers: [
        {
          id: 1,
          code: 'CUST-001',
          name: 'محمد أحمد',
          phone: '01012345678',
          address: '15 شارع الجمهورية، القاهرة',
          email: 'mohamed@example.com',
          creditLimit: 5000,
          currentBalance: 1250, // المبلغ المستحق
          totalPurchases: 25000,
          lastPurchaseDate: '2025-12-01',
          lastPaymentDate: '2025-11-25', // آخر دفعة
          paymentDueDays: 30, // مدة السداد المسموحة
          status: 'active', // active, inactive, blocked
          notes: 'عميل ممتاز',
          createdAt: '2025-01-15'
        },
        {
          id: 2,
          code: 'CUST-002',
          name: 'فاطمة علي',
          phone: '01198765432',
          address: '23 شارع النيل، الجيزة',
          email: 'fatma@example.com',
          creditLimit: 3000,
          currentBalance: 2850, // أقترب من الحد الأقصى
          totalPurchases: 15000,
          lastPurchaseDate: '2025-12-03',
          lastPaymentDate: '2025-10-15', // متأخر في السداد! 🚨
          paymentDueDays: 30,
          status: 'active',
          notes: '',
          createdAt: '2025-02-20'
        },
        {
          id: 3,
          code: 'CUST-003',
          name: 'أحمد حسن',
          phone: '01234567890',
          address: '45 شارع التحرير، الإسكندرية',
          email: '',
          creditLimit: 2000,
          currentBalance: 2500, // تجاوز الحد! 🚨
          totalPurchases: 8000,
          lastPurchaseDate: '2025-11-28',
          lastPaymentDate: '2025-09-20', // متأخر جداً! 🚨
          paymentDueDays: 30,
          status: 'active',
          notes: 'تجاوز الحد الائتماني',
          createdAt: '2025-03-10'
        }
      ],

      loading: false,
      error: null,

      // Actions

      /**
       * Fetch Customers from API
       */
      fetchCustomers: async () => {
        return await fetchAll(customersAPI.getAll, set, get, 'customers');
      },

      /**
       * Add new customer (CUST-001)
       * @param {Object} customer - Customer data
       */
      addCustomer: async (customer) => {
        try {
          // Validation (P034)
          if (!customer.name || !customer.name.trim()) {
            throw new Error('اسم العميل مطلوب');
          }

          if (!customer.phone || !customer.phone.trim()) {
            throw new Error('رقم الهاتف مطلوب');
          }

          // Phone validation (Egyptian format)
          const phoneRegex = /^01[0-2,5]{1}[0-9]{8}$/;
          if (!phoneRegex.test(customer.phone)) {
            throw new Error('رقم الهاتف غير صحيح (يجب أن يبدأ بـ 01)');
          }

          // Credit limit validation
          const creditLimit = parseFloat(customer.creditLimit) || 0;
          if (creditLimit < 0) {
            throw new Error('الحد الائتماني يجب أن يكون رقم موجب');
          }

          const generateLocal = (data) => ({
            id: Date.now(),
            code: `CUST-${String(Date.now()).slice(-6)}`,
            currentBalance: 0,
            totalPurchases: 0,
            lastPurchaseDate: null,
            creditLimit: creditLimit,
            ...data, // Spread data first to preserve status from form
            status: data.status ?? 1, // Default to 1 (active) if not provided
            createdAt: new Date().toISOString().split('T')[0]
          });

          return await createItem(customersAPI.create, customer, set, get, 'customers', generateLocal);
        } catch (error) {
          // Error Handling (P010)
          set({ error: error.message });
          throw error;
        }
      },

      /**
       * Update existing customer (CUST-001)
       * @param {number} id - Customer ID
       * @param {Object} updates - Fields to update
       */
      updateCustomer: async (id, updates) => {
        try {
          // Validation (P034)
          if (updates.phone) {
            const phoneRegex = /^01[0-2,5]{1}[0-9]{8}$/;
            if (!phoneRegex.test(updates.phone)) {
              throw new Error('رقم الهاتف غير صحيح');
            }
          }

          if (updates.creditLimit !== undefined) {
            const creditLimit = parseFloat(updates.creditLimit);
            if (creditLimit < 0) {
              throw new Error('الحد الائتماني يجب أن يكون رقم موجب');
            }
          }

          return await updateItem(customersAPI.update, id, updates, set, get, 'customers');
        } catch (error) {
          set({ error: error.message });
          throw error;
        }
      },

      /**
       * Delete customer (CUST-001)
       * @param {number} id - Customer ID
       */
      deleteCustomer: async (id) => {
        try {
          return await deleteItem(customersAPI.delete, id, set, get, 'customers');
        } catch (error) {
          set({ error: error.message });
          throw error;
        }
      },

      /**
       * Get customer by ID
       * @param {number} id - Customer ID
       * @returns {Object|null} Customer object or null
       */
      getCustomerById: (id) => {
        return get().customers.find((customer) => customer.id === id) || null;
      },

      /**
       * Get customer by code
       * @param {string} code - Customer code
       * @returns {Object|null} Customer object or null
       */
      getCustomerByCode: (code) => {
        return get().customers.find((customer) => customer.code === code) || null;
      },

      /**
       * Search customers by name or phone
       * @param {string} query - Search query
       * @returns {Array} Matching customers
       */
      searchCustomers: (query) => {
        if (!query || !query.trim()) return get().customers;

        const lowerQuery = query.toLowerCase().trim();
        return get().customers.filter(
          (customer) =>
            customer.name.toLowerCase().includes(lowerQuery) ||
            customer.phone.includes(lowerQuery) ||
            customer.code.toLowerCase().includes(lowerQuery) ||
            (customer.email && customer.email.toLowerCase().includes(lowerQuery))
        );
      },

      /**
       * Filter customers by status
       * @param {string} status - active, inactive, blocked
       * @returns {Array} Filtered customers
       */
      getCustomersByStatus: (status) => {
        return get().customers.filter((customer) => customer.status === status);
      },

      /**
       * Get customers with credit limit exceeded (CUST-002, CUST-004)
       * @returns {Array} Customers over credit limit
       */
      getCustomersOverCreditLimit: () => {
        return get().customers.filter(
          (customer) => customer.currentBalance > customer.creditLimit
        );
      },

      /**
       * Get customers nearing credit limit (CUST-002)
       * @param {number} threshold - Percentage threshold (default 80%)
       * @returns {Array} Customers approaching credit limit
       */
      getCustomersNearingCreditLimit: (threshold = 0.8) => {
        return get().customers.filter((customer) => {
          if (customer.creditLimit === 0) return false;
          const usageRatio = customer.currentBalance / customer.creditLimit;
          return usageRatio >= threshold && usageRatio <= 1;
        });
      },

      /**
       * Get overdue customers (customers who haven't paid within payment due days)
       * @returns {Array} Overdue customers with days overdue
       */
      getOverdueCustomers: () => {
        const today = new Date();
        return get().customers.filter((customer) => {
          if (!customer.lastPaymentDate || !customer.paymentDueDays) return false;
          if (customer.currentBalance === 0) return false;

          const lastPayment = new Date(customer.lastPaymentDate);
          const daysSincePayment = Math.floor((today - lastPayment) / (1000 * 60 * 60 * 24));

          return daysSincePayment > customer.paymentDueDays;
        }).map((customer) => {
          const lastPayment = new Date(customer.lastPaymentDate);
          const daysSincePayment = Math.floor((today - lastPayment) / (1000 * 60 * 60 * 24));
          const daysOverdue = daysSincePayment - customer.paymentDueDays;

          return {
            ...customer,
            daysOverdue
          };
        });
      },

      /**
       * Update customer balance (CUST-004)
       * @param {number} id - Customer ID
       * @param {number} amount - Amount to add (positive) or subtract (negative)
       */
      updateBalance: (id, amount) => {
        try {
          const customer = get().getCustomerById(id);
          if (!customer) {
            throw new Error('العميل غير موجود');
          }

          const newBalance = customer.currentBalance + amount;

          if (newBalance < 0) {
            throw new Error('الرصيد لا يمكن أن يكون سالب');
          }

          get().updateCustomer(id, { currentBalance: newBalance });
        } catch (error) {
          set({ error: error.message });
          throw error;
        }
      },

      /**
       * Check if customer can make purchase (CUST-002, CUST-004)
       * @param {number} id - Customer ID
       * @param {number} amount - Purchase amount
       * @returns {Object} {canPurchase: boolean, reason: string|null}
       */
      canMakePurchase: (id, amount) => {
        const customer = get().getCustomerById(id);

        if (!customer) {
          return { canPurchase: false, reason: 'العميل غير موجود' };
        }

        if (customer.status !== 'active') {
          return { canPurchase: false, reason: 'العميل غير نشط' };
        }

        const newBalance = customer.currentBalance + amount;

        if (newBalance > customer.creditLimit) {
          return {
            canPurchase: false,
            reason: `سيتجاوز الحد الائتماني (${customer.creditLimit} ج.م)`
          };
        }

        return { canPurchase: true, reason: null };
      },

      /**
       * Get total customers count
       * @returns {number} Total customers
       */
      getTotalCustomers: () => {
        return get().customers.length;
      },

      /**
       * Get active customers count
       * @returns {number} Active customers
       */
      getActiveCustomers: () => {
        return get().customers.filter((c) => c.status === 'active').length;
      },

      /**
       * Get total receivables (CUST-004)
       * @returns {number} Total balance across all customers
       */
      getTotalReceivables: () => {
        return get().customers.reduce((total, customer) => {
          const balance = parseFloat(customer.current_balance || customer.currentBalance || 0);
          return total + balance;
        }, 0);
      },

      /**
       * Get overdue customers (العملاء المتأخرين)
       * @returns {Promise<Array>} Overdue customers
       */
      fetchOverdueCustomers: async () => {
        set({ loading: true, error: null });
        try {
          const response = await customersAPI.getOverdue();
          console.log('Overdue customers API response:', response);
          const overdueCustomers = response.data.data?.customers || response.data.customers || [];
          console.log('Parsed overdue customers:', overdueCustomers);
          return overdueCustomers;
        } catch (error) {
          console.error('Fetch overdue customers error:', error);
          console.error('Error response:', error.response);
          const errorMessage = error.response?.data?.message || 'فشل في تحميل العملاء المتأخرين';
          set({ error: errorMessage });
          return []; // Return empty array instead of throwing
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Get customer statistics (إحصائيات العملاء)
       * @returns {Promise<Object>} Customer statistics
       */
      fetchCustomerStatistics: async () => {
        set({ loading: true, error: null });
        try {
          const response = await customersAPI.getStatistics();
          return response.data.data;
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل الإحصائيات';
          set({ error: errorMessage });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Get top customers by purchases (أعلى العملاء شراءً)
       * @param {number} limit - Number of customers to fetch
       * @returns {Promise<Array>} Top customers
       */
      fetchTopCustomers: async (limit = 10) => {
        set({ loading: true, error: null });
        try {
          const response = await customersAPI.getTop(limit);
          return response.data.data.customers;
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل أعلى العملاء';
          set({ error: errorMessage });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Get inactive customers (العملاء الراكدين)
       * @param {number} days - Days threshold
       * @returns {Promise<Array>} Inactive customers
       */
      fetchInactiveCustomers: async (days = 60) => {
        set({ loading: true, error: null });
        try {
          const response = await customersAPI.getInactive(days);
          return response.data.data.customers;
        } catch (error) {
          const errorMessage = error.response?.data?.message || 'فشل في تحميل العملاء الراكدين';
          set({ error: errorMessage });
          throw error;
        } finally {
          set({ loading: false });
        }
      },

      /**
       * Clear error
       */
      clearError: () => set({ error: null })
    }),
    {
      name: 'customer-storage', // LocalStorage key (P025)
      partialize: (state) => ({ customers: state.customers }) // Only persist customers
    }
  )
);

export default useCustomerStore;
